import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useDefaultSetItemRestSec } from '../../../hooks/use-default-set-item-rest-sec';
import { useProgramItemExercises } from '../../../hooks/useProgramItemExercises';
import { useGetUserProgramCollections } from '../../../redux/api/programsAPI';
import { IProgramItem } from '../../../types/interfaces/program-item';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { IProgramSetItem } from '../../../types/interfaces/program-set-item';
import { orderBy } from '../../../utils/collections/array-utils';
import {
  changeSetNumber,
  getEmptyProgramSetItem,
  orderOptions,
  orderSets,
} from '../../../utils/training/set-utils';
import { getProgramItemFormValidation } from './FormikData';

export function useEditProgramItemFormik(
  programItemId: string,
  initialValues: IProgramItem,
  onSubmit: (value: IProgramItem) => void,
) {
  const { navigate, back } = useNavigationStack();
  const [showSavedProgramItemModal, setShowSavedProgramItemModal] =
    useState(false);
  const { data: programCollections } = useGetUserProgramCollections();
  const programCollectionMap = orderBy(programCollections, 'title').map(
    (c) => ({ id: c.id, name: c.title }),
  );
  const { t } = useTranslation();
  const { exercises } = useProgramItemExercises({
    programItemId,
  });
  const { defaultRestForNewEmptyItem } = useDefaultSetItemRestSec();

  const formik = useFormik<IProgramItem>({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getProgramItemFormValidation(t),
    initialValues,
    onSubmit,
  });

  function onAddSavedProgramSet(sets: IProgramSet[], isSuperset: boolean) {
    const maxSetNumber = formik.values.programSets?.length
      ? Math.max(...formik.values.programSets.map((set) => set.setNumber))
      : -1;
    let orderNumber = 0;
    let setNumber = maxSetNumber + 1;

    const result: IProgramSet[] = [];

    sets.forEach((set) => {
      const { id, ...newProgramSet } = set;
      const exercise = exercises.find((e) => e.id === set.exerciseId);
      newProgramSet.programSetItems = set.programSetItems.map(
        (item: IProgramSetItem) => {
          const { id, ...newItem } = item;
          return {
            ...newItem,
            programSetItemOptions: orderOptions(
              item.programSetItemOptions.map((option) => {
                const { id, ...newOption } = option;

                return newOption;
              }),
              exercise?.options,
            ),
          };
        },
      );
      newProgramSet.setNumber = setNumber;
      newProgramSet.orderNumber = orderNumber;
      newProgramSet.exercise = exercise;

      if (isSuperset) {
        orderNumber++;
      } else {
        setNumber++;
      }

      if (programItemId) {
        newProgramSet.programItemId = programItemId;
      }

      result.push(newProgramSet);
    });

    formik.setFieldValue(
      'programSets',
      orderSets([...(formik.values.programSets || []), ...result]),
    );
  }

  function onAddEmptyProgramSet() {
    const maxSetNumber = formik.values.programSets?.length
      ? Math.max(...formik.values.programSets.map((set) => set.setNumber))
      : -1;

    const emptyProgramSet: IProgramSet = {
      programSetItems: [
        getEmptyProgramSetItem(0, [], defaultRestForNewEmptyItem),
      ],
      exerciseId: null,
      setNumber: maxSetNumber + 1,
      orderNumber: 0,
      programItemId: programItemId,
    };

    formik.setFieldValue(
      'programSets',
      orderSets([...(formik.values.programSets || []), emptyProgramSet]),
    );
  }

  function onCancelClick() {
    back();
  }

  function onChangeSetOrder(setNumber: number, direction: 'up' | 'down') {
    const maxSetNumber = Math.max(
      ...formik.values.programSets.map((set) => set.setNumber),
    );

    if (
      (direction === 'up' && setNumber <= 0) ||
      (direction === 'down' && setNumber >= maxSetNumber)
    )
      return;

    const updatedProgramSets = changeSetNumber(
      formik.values.programSets,
      setNumber,
      direction,
    );

    formik.setFieldValue('programSets', updatedProgramSets);
  }

  return {
    formik,
    onAddEmptyProgramSet,
    onAddSavedProgramSet,
    onCancelClick,
    onChangeSetOrder,
    showSavedProgramItemModal,
    setShowSavedProgramItemModal,
    programCollectionMap,
  };
}
